import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import { sanitizeInput, isValidUrl } from '../utils/validation'
import './Creations.css'

interface Creation {
  id: string
  title: string
  description: string
  authorId: string
  authorUsername: string
  category: string
  mediaUrl?: string
  createdAt: string
  likes: string[]
  about?: string
  techniques?: string[]
}

const Creations = () => {
  const { user } = useAuth()
  const { addNotification } = useNotifications()
  const [creations, setCreations] = useState<Creation[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('audio')
  const [mediaUrl, setMediaUrl] = useState('')
  const [about, setAbout] = useState('')
  const [techniques, setTechniques] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showLiked, setShowLiked] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchCreations = useCallback(async () => {
    const { data: creationsData, error: creationsError } = await supabase
      .from('creations')
      .select('*, profiles!creations_author_id_fkey(username)')
      .order('created_at', { ascending: false })

    if (creationsError) {
      console.error('Error fetching creations:', creationsError)
      setLoading(false)
      return
    }

    const { data: likesData } = await supabase
      .from('creation_likes')
      .select('creation_id, user_id')

    const mapped: Creation[] = (creationsData || []).map((c: Record<string, unknown>) => {
      const profile = c.profiles as Record<string, unknown> | null
      const cLikes = (likesData || [])
        .filter((l: Record<string, unknown>) => l.creation_id === c.id)
        .map((l: Record<string, unknown>) => l.user_id as string)

      return {
        id: c.id as string,
        title: c.title as string,
        description: (c.description as string) || '',
        authorId: c.author_id as string,
        authorUsername: (profile?.username as string) || 'unknown',
        category: (c.category as string) || 'audio',
        mediaUrl: (c.media_url as string) || undefined,
        about: (c.about as string) || undefined,
        techniques: (c.techniques as string[]) || undefined,
        createdAt: c.created_at as string,
        likes: cLikes,
      }
    })

    setCreations(mapped)
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchCreations()
  }, [fetchCreations])

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !title.trim() || !description.trim()) return

    const sanitizedTitle = sanitizeInput(title)
    const sanitizedDescription = sanitizeInput(description)
    const sanitizedAbout = about ? sanitizeInput(about) : undefined
    const sanitizedMediaUrl = mediaUrl ? sanitizeInput(mediaUrl) : undefined
    const techniquesArray = techniques.split(',').map(t => sanitizeInput(t.trim())).filter(t => t.length > 0)

    if (!sanitizedTitle.trim() || !sanitizedDescription.trim()) return
    if (sanitizedTitle.length > 200) return
    if (sanitizedDescription.length > 2000) return

    const { error: insertError } = await supabase
      .from('creations')
      .insert({
        author_id: user.id,
        title: sanitizedTitle.trim(),
        description: sanitizedDescription.trim(),
        category,
        media_url: sanitizedMediaUrl?.trim() || null,
        about: sanitizedAbout?.trim() || null,
        techniques: techniquesArray.length > 0 ? techniquesArray : [],
      })

    if (insertError) {
      console.error('Failed to create:', insertError)
      return
    }

    setTitle('')
    setDescription('')
    setCategory('audio')
    setMediaUrl('')
    setAbout('')
    setTechniques('')
    setIsUploading(false)
    await fetchCreations()
  }

  const handleToggleLike = async (creationId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!user) return

    const creation = creations.find(c => c.id === creationId)
    if (!creation) return

    const hasLiked = creation.likes.includes(user.id)

    // Optimistic update
    setCreations(prev => prev.map(c => {
      if (c.id === creationId) {
        const newLikes = hasLiked
          ? c.likes.filter(id => id !== user.id)
          : [...c.likes, user.id]
        return { ...c, likes: newLikes }
      }
      return c
    }))

    if (hasLiked) {
      await supabase
        .from('creation_likes')
        .delete()
        .eq('creation_id', creationId)
        .eq('user_id', user.id)
    } else {
      await supabase
        .from('creation_likes')
        .insert({ creation_id: creationId, user_id: user.id })

      if (creation.authorId !== user.id) {
        addNotification({
          type: 'like',
          title: 'New Like',
          message: `${user.username} liked your creation "${creation.title.substring(0, 50)}..."`,
          link: `/creation/${creationId}`,
          userId: creation.authorId,
        })
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const categories = ['all', 'audio', 'visual', 'interactive', 'installation', 'code']
  const [selectedCategory, setSelectedCategory] = useState('all')

  const filteredCreations = creations.filter(creat => {
    const categoryMatch = selectedCategory === 'all' || creat.category === selectedCategory
    const searchMatch = !searchQuery ||
      creat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creat.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creat.authorUsername.toLowerCase().includes(searchQuery.toLowerCase())
    const likedMatch = !showLiked || (user && creat.likes.includes(user.id))
    return categoryMatch && searchMatch && likedMatch
  })

  if (loading) {
    return (
      <div className="creations-container">
        <div className="creations-empty">
          <p>loading creations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="creations-container">
      <div className="creations-header">
        <h2 className="creations-title">community creations</h2>
        <p className="creations-subtitle">
          Share your work, get feedback, and discover what others are creating.
        </p>
      </div>

      {user && (
        <div className="creations-upload-section">
          {!isUploading ? (
            <button
              className="creations-upload-button"
              onClick={() => setIsUploading(true)}
            >
              <i className="fas fa-plus"></i>
              <span>share creation</span>
            </button>
          ) : (
            <form className="creations-upload-form" onSubmit={handleUpload}>
              <input
                type="text"
                className="creations-form-input"
                placeholder="creation title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <textarea
                className="creations-form-textarea"
                placeholder="describe your creation..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                required
              />
              <select
                className="creations-form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="audio">audio</option>
                <option value="visual">visual</option>
                <option value="interactive">interactive</option>
                <option value="installation">installation</option>
                <option value="code">code</option>
              </select>
              <div className="creations-form-row">
                <textarea
                  className="creations-form-textarea"
                  placeholder="about this creation (optional)"
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  rows={3}
                />
                <input
                  type="text"
                  className="creations-form-input"
                  placeholder="techniques used (comma-separated, e.g., Live coding, Sensor integration)"
                  value={techniques}
                  onChange={(e) => setTechniques(e.target.value)}
                />
                <input
                  type="url"
                  className="creations-form-input"
                  placeholder="media url (optional)"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                />
              </div>
              <div className="creations-form-actions">
                <button type="submit" className="creations-submit-button">
                  share
                </button>
                <button
                  type="button"
                  className="creations-cancel-button"
                  onClick={() => {
                    setIsUploading(false)
                    setTitle('')
                    setDescription('')
                    setCategory('audio')
                    setMediaUrl('')
                    setAbout('')
                    setTechniques('')
                  }}
                >
                  cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="creations-controls">
        <div className="creations-search">
          <input
            type="text"
            className="creations-search-input"
            placeholder="search creations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="fas fa-search"></i>
        </div>
        <div className="creations-filters">
          {user && (
            <button
              className={`creations-filter-button ${showLiked ? 'active' : ''}`}
              onClick={() => setShowLiked(!showLiked)}
            >
              <i className="fas fa-heart"></i>
              liked
            </button>
          )}
          {categories.map(cat => (
            <button
              key={cat}
              className={`creations-filter-button ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="creations-grid">
        {filteredCreations.length === 0 ? (
          <div className="creations-empty">
            <p>no creations yet. be the first to share!</p>
          </div>
        ) : (
          filteredCreations.map(creation => (
            <Link key={creation.id} to={`/creation/${creation.id}`} className="creation-card">
              <div className="creation-card-header">
                <div className="creation-author">
                  <div
                    className="creation-author-avatar"
                    style={{ background: generateGradientFromUsername(creation.authorUsername) }}
                    dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(creation.authorUsername, 36) }}
                  />
                  <div className="creation-author-info">
                    <Link
                      to={`/user/${creation.authorUsername}`}
                      className="creation-author-username"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {creation.authorUsername}
                    </Link>
                    <div className="creation-date">{formatDate(creation.createdAt)}</div>
                  </div>
                </div>
                <span className="creation-category-badge">{creation.category}</span>
              </div>
              <div className="creation-card-content">
                <h3 className="creation-card-title">{creation.title}</h3>
                <p className="creation-card-description">{creation.description}</p>
              </div>
              <div className="creation-card-footer">
                <button
                  className={`creation-like-button ${creation.likes.includes(user?.id || '') ? 'liked' : ''}`}
                  onClick={(e) => handleToggleLike(creation.id, e)}
                  disabled={!user}
                >
                  <i className="fas fa-heart"></i>
                  <span>{creation.likes.length}</span>
                </button>
                {creation.mediaUrl && isValidUrl(creation.mediaUrl) && (
                  <a href={creation.mediaUrl} target="_blank" rel="noopener noreferrer" className="creation-link">
                    <i className="fas fa-external-link-alt"></i>
                    view
                  </a>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  )
}

export default Creations
